import { useState, type FormEvent, type ReactElement } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthProvider';
import { ApiError } from '../../lib/api';
import { AuthLayout, AuthField, AuthSubmit, AuthError } from './AuthLayout';

export default function Login(): ReactElement {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname ?? '/app';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      // Bestandsnutzer ohne gesetztes Passwort -> zur Passwort-setzen-Seite leiten.
      if (err instanceof ApiError && err.code === 'MUST_SET_PASSWORD') {
        navigate('/passwort-vergessen', { state: { mustSet: true } });
        return;
      }
      setError(err instanceof ApiError ? err.message : 'Anmeldung fehlgeschlagen. Bitte erneut versuchen.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthLayout title="Willkommen zurück" subtitle="Melde dich bei deinem Saldify-Konto an.">
      <form onSubmit={handleSubmit} className="space-y-5" noValidate>
        {error && <AuthError message={error} />}
        <AuthField
          label="E-Mail-Adresse"
          type="email"
          autoComplete="email"
          value={email}
          onChange={setEmail}
          placeholder="name@firma.de"
          required
        />
        <AuthField
          label="Passwort"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={setPassword}
          placeholder="Dein Passwort"
          required
        />
        <AuthSubmit busy={busy}>Anmelden</AuthSubmit>
      </form>
      <p className="mt-4 text-center text-sm">
        <Link to="/passwort-vergessen" className="text-gray-500 hover:text-blue-600">
          Passwort vergessen oder erstmalig setzen?
        </Link>
      </p>
      <p className="mt-6 text-center text-sm text-gray-600">
        Noch kein Konto?{' '}
        <Link to="/register" className="font-semibold text-blue-600 hover:text-blue-700">
          Jetzt registrieren
        </Link>
      </p>
    </AuthLayout>
  );
}
